// Thrive-style city × service matrix.
//
// One source of truth for every {service, city} combination. Each combo gets:
//  - unique on-page modifier copy (so the page is not boilerplate)
//  - canonical URL builders
//  - sibling-city + sibling-service link helpers (silo-style internal linking)
//
// 2026-04: dropped "google-maps" from the city service set. The
// /{city}-local-seo route was retired (overlapped too closely with
// /{city}-seo). The /local-seo service hub itself remains as a top-level
// service page (not city-specific). Any historical SUPPRESSED_COMBOS entries
// for that combo are no longer needed.
//
// To add a new service, extend CityServiceKey + CITY_SERVICES in
// cityServicesData.ts. To add a new city, extend CITY_PAGES in citiesData.ts.

import { CITY_PAGES, type CityKey, type CityPage } from "./citiesData";
import { CITY_SERVICES, type CityServiceKey, type CityServiceConfig } from "./cityServicesData";

const SITE_URL = "https://coloradowebimpressions.com";

export type CityServiceCombo = {
  city: CityPage;
  service: CityServiceConfig;
  url: string; // absolute
  path: string; // relative
  modifier: string; // unique on-page sentence for this combo
};

/**
 * Per-combo modifier sentences. Missing entries get a deterministic fallback
 * so every page still has a unique line.
 */
const COMBO_MODIFIERS: Partial<Record<CityKey, Partial<Record<CityServiceKey, string>>>> = {
  "colorado-springs": {
    seo: "Springs SEO that wins for high-intent searches like 'Briargate plumber' and 'roofer near Powers Corridor' — not just the generic city-wide terms.",
    "web-design": "Springs websites built around the way local buyers actually shop — Military, PCS, and hail-season cycles factored into your CTAs.",
  },
  denver: {
    seo: "Denver SEO that goes neighborhood-deep — LoDo, RiNo, Cherry Creek, Highlands — because city-wide keywords alone won't beat the metro-sized agencies.",
    "web-design": "Denver websites engineered to convert in the metro's most competitive categories, where milliseconds and message clarity decide the click.",
  },
  pueblo: {
    seo: "Pueblo SEO that leans into the trust signals locals look for — long-tenured staff, real Pueblo phone numbers, and Pueblo West coverage built in.",
    "web-design": "Pueblo websites that feel local at first glance — copy and photography that read as hometown, not corporate.",
  },
  "castle-rock": {
    seo: "Castle Rock SEO tuned for premium buyers across The Meadows, Founders Village, and Castle Pines — quality positioning, not lowest-price keywords.",
    "web-design": "Castle Rock websites that match the price tag of your work — polished UX, real photography, and trust signals worthy of higher-end buyers.",
  },
  monument: {
    seo: "Monument and Tri-Lakes SEO that captures both Palmer Lake locals and the spillover from north Colorado Springs in a single hybrid strategy.",
    "web-design": "Monument websites built for the Tri-Lakes market — where buyers split their time between Monument, Gleneagle, and north Springs.",
  },
  "fort-collins": {
    seo: "Fort Collins SEO with content depth that respects an educated buyer — and a Northern Colorado footprint covering Loveland, Windsor, and Wellington.",
    "web-design": "Fort Collins websites built for a savvy, sustainability-minded audience — values surfaced honestly, never astroturfed.",
  },
  boulder: {
    seo: "Boulder SEO that wins on substance — story-led content for a sophisticated audience, plus reach into Louisville, Lafayette, and Niwot.",
    "web-design": "Boulder websites that lead with story and values, designed for an audience that reads before it buys.",
  },
  parker: {
    seo: "Parker SEO that ranks ahead of the demand curve in one of Colorado's fastest-growing markets — with The Pinery, Stonegate, and Stroh Ranch built in.",
    "web-design": "Parker websites tuned for family buyers and new movers — clear offers, fast load times, easy contact.",
  },
  aurora: {
    seo: "Aurora SEO that goes neighborhood-deep across Southlands, Saddle Rock, and the Anschutz/Fitzsimons corridor — plus Denver, Centennial, and Parker overflow built in.",
    "web-design": "Aurora websites built for family corridors like Southlands and Saddle Rock and the medical-adjacent buyers around Anschutz — fast, mobile-first, and conversion-tuned.",
  },
  lakewood: {
    seo: "Lakewood SEO tuned for Belmar retail, Green Mountain and Bear Creek residential trades, and the foothills overflow into Golden, Morrison, and Wheat Ridge.",
    "web-design": "Lakewood websites built for Belmar's walkable retail buyers and Green Mountain's family-trade customers — different funnels for different neighborhoods.",
  },
  thornton: {
    seo: "Thornton SEO tuned for the 144th and I-25 growth corridor — Trail Winds, Eastlake, and Skylake Ranch — plus a north-metro footprint into Westminster, Northglenn, and Brighton.",
    "web-design": "Thornton websites built for new-mover families along the 144th corridor — fast, mobile-first, and conversion-tuned to intercept demand before national franchises do.",
  },
  arvada: {
    seo: "Arvada SEO that wins Olde Town's heritage retail and the new-build trade demand in Candelas, Leyden Rock, and Whisper Creek — with Wheat Ridge and Golden overflow built in.",
    "web-design": "Arvada websites for two buyer profiles in one city — Olde Town's tenured-trust shoppers and Candelas's new-mover families — with the polish each one expects.",
  },
  westminster: {
    seo: "Westminster SEO built for a two-county footprint — Adams to the north, Jefferson to the south — plus US-36 corridor commercial demand from FlatIron Crossing and the tech offices.",
    "web-design": "Westminster websites that handle three audiences in one site: residential trades, US-36 corridor commercial buyers, and the family corridors around Standley Lake and Legacy Ridge.",
  },
  centennial: {
    seo: "Centennial SEO tuned for premium buyers in Willow Creek, Heritage Greens, and Saddle Rock Golf Club — plus DTC-adjacent commercial demand and south-metro overflow into Lone Tree.",
    "web-design": "Centennial websites that match the price tag of premium work — polished UX, real photography, and trust signals worthy of one of Colorado's most affluent markets.",
  },
};

function fallbackModifier(city: CityPage, service: CityServiceConfig): string {
  return `${service.serviceName} in ${city.name} built around how ${city.region} customers actually search — neighborhood-specific signals across ${city.neighborhoods.slice(0, 3).join(", ")}, plus the broader ${city.region} footprint.`;
}

export function buildComboPath(serviceKey: CityServiceKey, citySlug: string): string {
  const svc = CITY_SERVICES[serviceKey];
  // Web design uses keyword-first slug for SEO (except Colorado Springs which
  // keeps its legacy high-equity URL): /web-design-{city}-colorado.
  if (svc.routeBase === "web-design" && citySlug !== "colorado-springs") {
    return `/web-design-${citySlug}-colorado`;
  }
  // Flat URL format: /{citySlug}-{routeBase}, e.g. /denver-seo, /web-design-aurora-colorado
  return `/${citySlug}-${svc.routeBase}`;
}

export function buildComboUrl(serviceKey: CityServiceKey, citySlug: string): string {
  return `${SITE_URL}${buildComboPath(serviceKey, citySlug)}`;
}

export function getCombo(
  serviceKey: CityServiceKey,
  citySlug: string,
): CityServiceCombo | null {
  const city = CITY_PAGES.find((c) => c.slug === citySlug);
  const service = CITY_SERVICES[serviceKey];
  if (!city || !service) return null;
  const modifier =
    COMBO_MODIFIERS[city.slug as CityKey]?.[serviceKey] ?? fallbackModifier(city, service);
  return {
    city,
    service,
    url: buildComboUrl(serviceKey, city.slug),
    path: buildComboPath(serviceKey, city.slug),
    modifier,
  };
}

/**
 * Suppression hook (currently empty after the 2026-04 local-seo retirement).
 * Kept as the pattern for future per-combo internal-link suppression: if a
 * spoke page exists but should not be linked from elsewhere on the site, add
 * its `{ citySlug, service }` here and every link generator below filters it.
 */
const SUPPRESSED_COMBOS: Array<{ citySlug: string; service: CityServiceKey }> = [];

export function isSuppressedCombo(citySlug: string, serviceKey: CityServiceKey): boolean {
  return SUPPRESSED_COMBOS.some(
    (s) => s.citySlug === citySlug && s.service === serviceKey,
  );
}

/** Every linkable combo in the matrix — used by sitemap, llms.txt, and the
 *  /locations index. Suppressed combos are excluded. */
export function getAllCombos(): CityServiceCombo[] {
  const combos: CityServiceCombo[] = [];
  for (const service of Object.values(CITY_SERVICES)) {
    for (const city of CITY_PAGES) {
      if (isSuppressedCombo(city.slug, service.key)) continue;
      const combo = getCombo(service.key, city.slug);
      if (combo) combos.push(combo);
    }
  }
  return combos;
}

/** Other cities for the same service — used for in-page sibling links. */
export function getSiblingCityCombos(
  serviceKey: CityServiceKey,
  excludeCitySlug: string,
): CityServiceCombo[] {
  return CITY_PAGES.filter(
    (c) => c.slug !== excludeCitySlug && !isSuppressedCombo(c.slug, serviceKey),
  )
    .map((c) => getCombo(serviceKey, c.slug))
    .filter((x): x is CityServiceCombo => Boolean(x));
}

/** Other services in the same city — used for in-page sibling links. */
export function getSiblingServiceCombos(
  citySlug: string,
  excludeServiceKey: CityServiceKey,
): CityServiceCombo[] {
  return Object.values(CITY_SERVICES)
    .filter((s) => s.key !== excludeServiceKey && !isSuppressedCombo(citySlug, s.key))
    .map((s) => getCombo(s.key, citySlug))
    .filter((x): x is CityServiceCombo => Boolean(x));
}

/** All service combos for a given city — used by city hub pages to link to
 *  every service spoke for that city. */
export function getCombosForCity(citySlug: string): CityServiceCombo[] {
  return Object.values(CITY_SERVICES)
    .filter((s) => !isSuppressedCombo(citySlug, s.key))
    .map((s) => getCombo(s.key, citySlug))
    .filter((x): x is CityServiceCombo => Boolean(x));
}

/**
 * Anchor text variants for city × service internal links. Goal: avoid
 * identical sitewide anchors while keeping the primary keyword in every
 * variant. Rotate by stable index for SEO stability and SSR consistency.
 */
const CITY_ANCHOR_PATTERNS: Record<
  CityServiceKey,
  ((city: string) => string)[]
> = {
  seo: [
    (c) => `SEO services in ${c}`,
    (c) => `${c} SEO company`,
    (c) => `Local SEO for ${c} businesses`,
    (c) => `Search engine optimization in ${c}`,
  ],
  "web-design": [
    (c) => `Web design in ${c}`,
    (c) => `${c} website design company`,
    (c) => `Custom websites for ${c} businesses`,
    (c) => `Website design and development in ${c}`,
  ],
};

export function getCityAnchorVariant(
  serviceKey: CityServiceKey,
  cityName: string,
  index: number,
): string {
  const patterns = CITY_ANCHOR_PATTERNS[serviceKey];
  if (!patterns || patterns.length === 0) {
    return `${CITY_SERVICES[serviceKey].serviceName} in ${cityName}`;
  }
  return patterns[index % patterns.length](cityName);
}

export const ALL_SERVICE_KEYS: CityServiceKey[] = Object.values(CITY_SERVICES).map((s) => s.key);
